import React, { useRef } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { removeDialog } from 'app-components/flux/root-ui/rootUiActions';

import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import useStyles from './styles';
import type { AlertDialogProps } from './types';

const AlertDialog = ({
  buttons,
  content,
  dialogId,
  onClose,
  showCloseButton,
  tertiaryButton,
  title,
  titleIcon,
}: AlertDialogProps): ReactElement => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const customDialogButtons = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'customDialogButtons'),
  );
  const closedRef = useRef(false);

  const handleClose = (btnPressed: string) => {
    onClose?.({ btnPressed });
    dispatch(removeDialog(dialogId));
    closedRef.current = true;
  };

  // Enter presses the last button unless a button has focus. Escape reaches onClose.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement) && buttons.length > 0) {
      handleClose(buttons[buttons.length - 1]);
    }
    // Closing returns focus to the opener, and the browser would click it with this Enter.
    if (closedRef.current) {
      event.preventDefault();
    }
  };

  const btn1Variant = customDialogButtons ? 'outlined' : 'text';
  const btn2Variant = customDialogButtons ? 'contained' : 'text';
  const actionButtons = buttons.map((btn, index) => (
    <QButton
      variant={index === buttons.length - 1 ? btn2Variant : btn1Variant}
      id={`alrtdlg:${btn}`}
      key={`alrtdlg:${btn}`}
      onClick={() => handleClose(btn)}
      color='primary'
      style={{ marginLeft: 8 }}
    >
      {btn}
    </QButton>
  ));

  return (
    <StdDialog
      aria-describedby='alert-dialog-description'
      dialogId={dialogId}
      onClose={() => handleClose('Escape')}
      onKeyDown={handleKeyDown}
      open
      sharedcomponentid='DIALOG_ALERT'
      showCloseButton={showCloseButton}
      title={
        <>
          {titleIcon}
          {title}
        </>
      }
    >
      <DialogContent>
        <DialogContent id='alert-dialog-description' className={classes.textContent} style={{ paddingTop: 16 }}>
          {typeof content === 'string' ? (
            <Typography className={classes.textArea} variant='titleRegular'>
              {content}
            </Typography>
          ) : (
            content
          )}
        </DialogContent>
      </DialogContent>

      {tertiaryButton ? (
        <DialogActions
          style={{ paddingBottom: 24, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
        >
          <QButton
            variant='text'
            id={`alrtdlg:${tertiaryButton}`}
            onClick={() => handleClose(tertiaryButton)}
            color='primary'
          >
            {tertiaryButton}
          </QButton>
          <div style={{ marginRight: 0 }}>{actionButtons}</div>
        </DialogActions>
      ) : (
        <DialogActions style={{ paddingBottom: 24, paddingRight: 24, marginRight: 0 }}>{actionButtons}</DialogActions>
      )}
    </StdDialog>
  );
};

export default AlertDialog;
